import { Component, type ReactNode } from 'react';
import { isChunkLoadError } from '@/utils/lazyWithRetry';

interface ChunkErrorBoundaryProps {
  children: ReactNode;
  /**
   * Render prop for the error UI. Receives a `retry` callback that resets the
   * boundary, a `hardReload` callback that bypasses caches, and the captured
   * error for diagnostic display.
   */
  fallback: (args: {
    retry: () => void;
    hardReload: () => void;
    error: Error | null;
  }) => ReactNode;
  /** Optional key — when it changes, the boundary auto-resets. */
  resetKey?: string | number;
}

interface ChunkErrorBoundaryState {
  error: Error | null;
  resetKey: string | number | undefined;
}

/**
 * Error boundary tuned for lazy-loaded route chunks.
 *
 * - Catches render-phase errors (including dynamic import failures bubbled
 *   up by React.lazy + Suspense).
 * - For known chunk-load errors, gives the user a clear retry path instead
 *   of a blank screen.
 * - Provides a "hard reload" that drops the cached HTML so the browser can
 *   pick up new chunk hashes after a redeploy.
 */
class ChunkErrorBoundary extends Component<
  ChunkErrorBoundaryProps,
  ChunkErrorBoundaryState
> {
  state: ChunkErrorBoundaryState = { error: null, resetKey: this.props.resetKey };

  static getDerivedStateFromError(error: Error): Partial<ChunkErrorBoundaryState> {
    return { error };
  }

  static getDerivedStateFromProps(
    props: ChunkErrorBoundaryProps,
    state: ChunkErrorBoundaryState,
  ): Partial<ChunkErrorBoundaryState> | null {
    if (props.resetKey !== state.resetKey) {
      return { error: null, resetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error: Error) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.error('[ChunkErrorBoundary]', error);
    }
  }

  retry = () => {
    this.setState({ error: null });
  };

  hardReload = () => {
    if (typeof window === 'undefined') return;
    try {
      // Clear the throttle so lazyWithRetry will reload again if needed.
      sessionStorage.removeItem('chunk-reload-at');
    } catch {
      /* ignore storage errors (private mode, etc.) */
    }
    // Cache-busting param ensures the HTML (and therefore the chunk manifest)
    // is fetched fresh rather than served from the disk cache.
    const url = new URL(window.location.href);
    url.searchParams.set('_r', String(Date.now()));
    window.location.replace(url.toString());
  };

  render() {
    if (this.state.error) {
      // Surface chunk-load errors first; for unrelated render errors we still
      // render the friendly UI rather than crashing the route.
      void isChunkLoadError(this.state.error);
      return this.props.fallback({
        retry: this.retry,
        hardReload: this.hardReload,
        error: this.state.error,
      });
    }
    return this.props.children;
  }
}

export default ChunkErrorBoundary;